// 職責：把 public/config.js 注入的 window.__SHOPMGR__ 收斂成單一、已正規化且必定存在的設定物件

/*
 * 為什麼要有這一層（doc 06 §10.4 / doc 09 §8.2）：
 *   API 位址若編譯進 bundle，每個品牌就得各建置一份 —— 這正是 legacy vite.config.js
 *   留下四個被註解掉的 base 的成因。位址留到執行期後，同一份 dist/ 可部署到任何品牌。
 *
 * 為什麼只在這裡讀 window.__SHOPMGR__：
 *   若各處自行讀取，「忘了給預設值」與「結尾斜線有無不一致」就會各自重演一次。
 *   本檔是唯一讀取點，其餘模組一律 import runtimeConfig。
 */

export interface ShopmgrRuntimeConfig {
  opsApiBase: string
  /**
   * menu-pipeline AI 側車基址（doc menu-entry-ai-design §5.1）。
   * 與 scpos 的 /voice/* 同一個側車、同一個 nginx upstream。
   * ★ 根路徑部署必須用 `/menu-api/`，不可以用 `/menu/` —— 後者撞 SPA 的 `/menu/:id`
   *   （見 deploy/config.shopmgr.scpos.online.js）。
   */
  menuApiBase: string
  /** 側車若設 MENU_IMAGE_INBOUND_TOKEN，前端要帶 X-Menu-Image-Token；同源部署可留空 */
  menuApiToken?: string
  brand?: string
  title?: string
}

/** config.js 未部署（或本機 vite dev）時的保底值，與 doc 02 §1.1 的預設一致 */
const DEFAULT_OPS_API_BASE = '/ops-console/api/'
/** 與正式根路徑部署對齊；舊 `/taiyi/shopmgr/v2/` 前綴式部署才可以用 `/menu/` */
const DEFAULT_MENU_API_BASE = '/menu-api/'
const DEFAULT_TITLE = 'ShopManager'

/**
 * 統一補上結尾斜線。
 * 理由：下游一律用「base + 相對路徑」拼接，若 base 有時帶斜線有時不帶，
 * 就會拼出 `/ops-console/apiauth/login` 這種只在特定部署環境才出現的錯誤。
 */
function normalizeBase(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback
  const trimmed = value.trim()
  if (trimmed === '') return fallback
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`
}

function readOptionalText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function readRuntimeConfig(): ShopmgrRuntimeConfig {
  // 單元測試（node 環境）與未來可能的 SSR 都沒有 window，這裡不能假設它存在
  const injected = typeof window === 'undefined' ? undefined : window.__SHOPMGR__

  return {
    opsApiBase: normalizeBase(injected?.opsApiBase, DEFAULT_OPS_API_BASE),
    menuApiBase: normalizeBase(injected?.menuApiBase, DEFAULT_MENU_API_BASE),
    menuApiToken: readOptionalText(injected?.menuApiToken),
    brand: readOptionalText(injected?.brand),
    title: readOptionalText(injected?.title) ?? DEFAULT_TITLE,
  }
}

/**
 * 執行期設定的單一事實來源。
 * 在模組載入時求值一次：config.js 是 classic script、必定早於 module 執行（doc 06 §10.4），
 * 且部署後不會於執行期改變，凍結成常數可避免各處讀到不同快照。
 */
export const runtimeConfig: ShopmgrRuntimeConfig = readRuntimeConfig()
